import { useEffect, useState } from 'react';
import { authClient } from '../../lib/auth-client';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { BrandMark } from '../shell/BrandMark';
import { identifyClient } from '@servwell/shared';
import { oauthQueryFromSearch, redirectUrlFrom } from '../../lib/oauth-continue';

/**
 * `/consent` — the screen where a shop owner grants an AI tool access to their shop.
 *
 * Better Auth's provider is configured with `consentPage: '/consent'` and redirects here with
 * the signed authorization query. Until this route existed the redirect hit the SPA fallback,
 * the gate drew the app, and the authorization silently never completed — no code was ever
 * issued, no error was ever shown, and `mcp.test.ts` could not see any of it because it stubs
 * the token verifier and never walks this flow.
 *
 * ⚠️ THIS IS A SECURITY SCREEN, and its only real job is to let the owner answer "is this the
 * thing I just tried to connect?". So the client's own registered name is fetched and shown
 * rather than assumed — anyone on the internet can register a client (that is the point of
 * zero-config connectors), which means the name is attacker-chosen and the owner is the only
 * one who can recognise it. It is rendered as text, never as a link or an image.
 */

/**
 * What the four scopes we grant actually mean, said plainly.
 *
 * NOT a scope→data map, because these are identity scopes and do not describe data access at
 * all. What the AI can read is decided by the MCP tool registry (`apps/api/src/mcp/reads/`,
 * read-only by construction) and by the signed-in member's own role. Listing "profile" and
 * "email" as though they were the extent of it would be true about OAuth and misleading about
 * the product, which is the kind of accurate-but-false screen this codebase refuses to ship.
 */
const SCOPE_COPY: Record<string, string> = {
  openid: 'Confirm who you are',
  profile: 'Your name',
  email: 'Your email address',
  offline_access: 'Stay connected without asking you to sign in again',
};

interface PublicClient {
  client_name?: string | null;
  name?: string | null;
}

export function ConsentPage() {
  const oauthQuery = oauthQueryFromSearch(window.location.search);
  const params = new URLSearchParams(oauthQuery ?? '');
  const clientId = params.get('client_id') ?? '';
  const scopes = (params.get('scope') ?? '').split(' ').filter(Boolean);

  /**
   * 🔴 WHERE THE CODE ACTUALLY GOES — the one fact on this screen the owner can CHECK.
   *
   * `client_name` is self-asserted at registration, and registration is open to anyone. So a
   * stranger can register as "Claude", point `redirect_uri` at their own server, and send a
   * shop owner a perfectly valid authorization URL. The owner reads "Claude", approves, and the
   * authorization code is delivered to the attacker. A consent screen showing only the name is
   * an impersonation surface, not a safeguard.
   *
   * ⚠️ AND IT IS NOT AUTHENTICATION. An earlier version of this comment called it "the only
   * identity signal the attacker does not choose", which is false: with open registration the
   * attacker picks the registered redirect host too, so `claude.ai.evil.test` or a homograph
   * is entirely available to them. What this buys is that the displayed host CANNOT disagree
   * with where the code goes — Better Auth validates redirect_uri against the registered set
   * before this screen renders — so the owner is shown the real destination rather than only
   * a name. It downgrades impersonation from undetectable to phishing. Actually closing it
   * needs a verified or allowlisted client, which is what pre-registering Claude and ChatGPT
   * is for. Origin only: a full URL invites padding the path with reassuring words.
   */
  const redirectHost = (() => {
    const raw = params.get('redirect_uri');
    if (!raw) return null;
    try {
      return new URL(raw).host;
    } catch {
      return null;
    }
  })();

  /**
   * ⭐ THE NAME SHOWN FOR A KNOWN CLIENT COMES FROM US, NOT FROM THE CLIENT.
   *
   * `identifyClient` reads only the redirect ORIGIN — the field Better Auth has already
   * validated against the client's registered set before this screen renders, and the one an
   * attacker cannot obtain. It never reads `client_name`, so a stranger registering as
   * "Claude" gets no badge and no canonical name: there is no attacker-controlled string
   * anywhere in the verified path.
   *
   * ⚠️ Everything unknown, loopback, or non-https resolves to unverified, which renders
   * exactly as this screen did before — the self-asserted name, labelled, beside the real
   * host. Claude Code is deliberately in that bucket: it authorizes over
   * `http://localhost:<port>/callback`, which any process on the owner's machine can bind.
   */
  const identity = identifyClient(params.get('redirect_uri'));

  const [clientName, setClientName] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'approve' | 'deny'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await authClient.$fetch<PublicClient>(
          `/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
          { method: 'GET' },
        );
        const data = (res as { data?: PublicClient })?.data;
        if (!cancelled) setClientName(data?.client_name ?? data?.name ?? null);
      } catch {
        // Leave it null. The fallback below names the shortfall rather than inventing a name.
        if (!cancelled) setClientName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  async function decide(accept: boolean) {
    setBusy(accept ? 'approve' : 'deny');
    setError(null);
    try {
      const res = await authClient.$fetch('/oauth2/consent', {
        method: 'POST',
        body: { accept, oauth_query: oauthQuery },
      });
      const url = redirectUrlFrom((res as { data?: unknown })?.data);
      if (!url) {
        setError(
          'The app that asked for access could not be reached. Start again from that app.',
        );
        return;
      }
      // Leaving the SPA for the connector's own origin — a router push cannot get there.
      window.location.assign(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Reached without a signed authorization — a bookmark, a refresh after the code was used,
  // or someone typing the path. There is nothing to approve, and pretending otherwise would
  // train owners to approve screens that arrived from nowhere.
  if (!oauthQuery || !clientId) {
    return (
      <Shell>
        <SectionCard title="Nothing to approve">
          <p className="text-sm text-muted-foreground">
            This page only works when an app sends you here to ask for access. Start from
            the app you want to connect.
          </p>
        </SectionCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <SectionCard title="Connect an app to your shop">
        <div className="space-y-4">
          <p className="text-sm">
            <strong className="font-semibold">
              {identity.verified
                ? identity.name
                : (clientName ?? 'An application that did not give a name')}
            </strong>{' '}
            {identity.verified ? (
              <span
                data-testid="verified-badge"
                className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-xs font-semibold"
              >
                Verified
              </span>
            ) : null}{' '}
            is asking to read your Servwell data.
          </p>

          {/*
            Stated as plainly as the name, and deliberately next to it. The name is what the
            app calls itself; this is where your data goes. Anyone can choose the first.
          */}
          <div className="rounded-[var(--radius)] border border-border bg-surface-2 p-3">
            <p className="text-xs text-muted-foreground">Access will be sent to</p>
            <p className="break-all text-sm font-semibold">
              {redirectHost ?? 'an address we could not read'}
            </p>
            {identity.verified ? (
              <p className="mt-1 text-xs text-muted-foreground">
                This is {identity.name}&rsquo;s official address, which is why we can
                vouch for the name above.
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Any app can call itself anything. If you don&rsquo;t recognise this
                address, deny it.
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold">It will be able to:</p>
            <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
              {scopes.map((s) => (
                <li key={s}>• {SCOPE_COPY[s] ?? s}</li>
              ))}
              <li>• Read your jobs, customers, quotes and invoices</li>
            </ul>
          </div>

          <div>
            <p className="text-sm font-semibold">It will not be able to:</p>
            <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
              {/* True by construction, not by promise: the MCP registry exposes reads only. */}
              <li>• Change, create or delete anything</li>
              <li>• See more than your own role lets you see</li>
            </ul>
          </div>

          {/*
            🔴 THIS USED TO PROMISE "disconnect at any time in Settings, and it loses access
            immediately". It is not true at the moment it matters most. The Connected-AI card is
            created by `recordMcpCall` (apps/api/src/mcp/audit.ts) — on the first tool call, not
            on approval. So an owner who approves, thinks better of it, and goes straight to
            Settings finds NOTHING there to disconnect, having just been told they could.

            The honest version says what is actually true, and the sentence it is replaced with
            is the one that would have made that owner deny instead.
          */}
          <p className="text-xs text-muted-foreground">
            It appears in Settings once it first reads something, and you can disconnect
            it there from then on. Approve it only if you meant to connect it.
          </p>

          {/*
            Suppressed when the origin verifies: the name is then OURS, so "did not register a
            name" would be both false and alarming. An unverified client keeps the warning —
            that is the case where the owner is the only one who can tell.
          */}
          {!identity.verified && clientName === null ? (
            <Notice tone="warn">
              This app did not register a name. If you were not expecting this, deny it.
            </Notice>
          ) : null}

          {error ? <Notice tone="danger">{error}</Notice> : null}

          <div className="flex gap-2">
            <Button
              onClick={() => void decide(true)}
              disabled={busy !== null}
              className="flex-1"
            >
              {busy === 'approve' ? 'One moment…' : 'Allow access'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => void decide(false)}
              disabled={busy !== null}
              className="flex-1"
            >
              {busy === 'deny' ? 'One moment…' : 'Deny'}
            </Button>
          </div>
        </div>
      </SectionCard>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-5 p-6">
      <div className="flex items-center gap-3">
        <BrandMark className="size-11" />
        <div>
          <h1 className="font-heading text-xl font-bold">Servwell</h1>
        </div>
      </div>
      {children}
    </div>
  );
}
